import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Building, ArrowLeft, Plus, X } from 'lucide-react';
import { API, useAuth } from '@/App';

const RegisterBusiness = () => {
  const navigate = useNavigate();
  const { businessId } = useParams();
  const isEditMode = !!businessId;
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([
    'Restaurant & Cafe', 'Legal Services', 'Real Estate', 'Healthcare', 'Education',
    'Technology', 'Financial Services', 'Beauty & Wellness', 'Retail', 'Consulting',
    'Transportation', 'Fitness', 'Other'
  ]);
  const [newService, setNewService] = useState('');
  const [newLanguage, setNewLanguage] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    subcategory: '',
    logo: '',
    cover_image: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    city: '',
    country: '',
    is_expat_owned: false,
    is_expat_friendly: true,
    services_offered: [],
    languages_spoken: []
  });

  useEffect(() => {
    if (!user) {
      toast.error('Please sign in to register a business');
      navigate('/');
      return;
    }
    fetchCategories();
    if (isEditMode) {
      fetchBusinessData();
    }
  }, [user]);

  const fetchBusinessData = async () => {
    try {
      const response = await axios.get(`${API}/businesses/${businessId}`);
      const b = response.data;
      setFormData({
        name: b.name || '',
        description: b.description || '',
        category: b.category || '',
        subcategory: b.subcategory || '',
        logo: b.logo || '',
        cover_image: b.cover_image || '',
        email: b.email || '',
        phone: b.phone || '',
        website: b.website || '',
        address: b.address || '',
        city: b.city || '',
        country: b.country || '',
        is_expat_owned: b.is_expat_owned || false,
        is_expat_friendly: b.is_expat_friendly ?? true,
        services_offered: b.services_offered || [],
        languages_spoken: b.languages_spoken || []
      });
    } catch (error) {
      toast.error('Failed to load business data');
      navigate('/businesses');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/businesses/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const addService = () => {
    if (newService.trim() && !formData.services_offered.includes(newService.trim())) {
      setFormData({
        ...formData,
        services_offered: [...formData.services_offered, newService.trim()]
      });
      setNewService('');
    }
  };

  const removeService = (service) => {
    setFormData({
      ...formData,
      services_offered: formData.services_offered.filter(s => s !== service)
    });
  };

  const addLanguage = () => {
    if (newLanguage.trim() && !formData.languages_spoken.includes(newLanguage.trim())) {
      setFormData({
        ...formData,
        languages_spoken: [...formData.languages_spoken, newLanguage.trim()]
      });
      setNewLanguage('');
    }
  };

  const removeLanguage = (lang) => {
    setFormData({
      ...formData,
      languages_spoken: formData.languages_spoken.filter(l => l !== lang)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.description || !formData.category || !formData.city || !formData.country) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      if (isEditMode) {
        await axios.put(`${API}/businesses/${businessId}`, formData);
        toast.success('Business listing updated!');
        navigate(`/businesses/${businessId}`);
      } else {
        await axios.post(`${API}/businesses`, formData);
        toast.success('Business registered! It will be visible after admin approval.');
        navigate('/businesses');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register business');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <button onClick={() => navigate('/businesses')} className="inline-flex items-center text-muted-foreground hover:text-foreground mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Directory
          </button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                <Building className="w-8 h-8 text-primary" />
              </div>
              <h1 className="text-3xl font-heading font-bold mb-2">{isEditMode ? 'Edit Business' : 'Register Your Business'}</h1>
              <p className="text-muted-foreground">List your business in our expat-friendly directory</p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 border-2 border-border space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h2 className="text-xl font-bold">Basic Information</h2>
                
                <div>
                  <Label htmlFor="name">Business Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your business name"
                    className="mt-1"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="category">Category *</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Tell us about your business, what services you offer, and why expats should choose you..."
                    className="mt-1 min-h-[120px]"
                    required
                  />
                </div>

                <div className="flex flex-col gap-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="expat_owned"
                      checked={formData.is_expat_owned}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_expat_owned: checked })}
                    />
                    <Label htmlFor="expat_owned" className="cursor-pointer">
                      This is an expat-owned business
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="expat_friendly"
                      checked={formData.is_expat_friendly}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_expat_friendly: checked })}
                    />
                    <Label htmlFor="expat_friendly" className="cursor-pointer">
                      This business is expat-friendly (English speaking staff, etc.)
                    </Label>
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="space-y-4 pt-6 border-t border-border">
                <h2 className="text-xl font-bold">Location</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="e.g., Bangkok"
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">Country *</Label>
                    <Input
                      id="country"
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      placeholder="e.g., Thailand"
                      className="mt-1"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="address">Address (Optional)</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Street address"
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Contact */}
              <div className="space-y-4 pt-6 border-t border-border">
                <h2 className="text-xl font-bold">Contact Information</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="contact@business.com"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+66 123 456 789"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://www.yourbusiness.com"
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Services */}
              <div className="space-y-4 pt-6 border-t border-border">
                <h2 className="text-xl font-bold">Services Offered</h2>
                
                <div className="flex gap-2">
                  <Input
                    value={newService}
                    onChange={(e) => setNewService(e.target.value)}
                    placeholder="Add a service..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addService())}
                  />
                  <Button type="button" variant="outline" onClick={addService}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {formData.services_offered.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.services_offered.map((service, index) => (
                      <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-muted rounded-full text-sm">
                        {service}
                        <button type="button" onClick={() => removeService(service)} className="hover:text-destructive">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Languages */}
              <div className="space-y-4 pt-6 border-t border-border">
                <h2 className="text-xl font-bold">Languages Spoken</h2>
                
                <div className="flex gap-2">
                  <Input
                    value={newLanguage}
                    onChange={(e) => setNewLanguage(e.target.value)}
                    placeholder="Add a language..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLanguage())}
                  />
                  <Button type="button" variant="outline" onClick={addLanguage}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {formData.languages_spoken.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.languages_spoken.map((lang, index) => (
                      <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-muted rounded-full text-sm">
                        {lang}
                        <button type="button" onClick={() => removeLanguage(lang)} className="hover:text-destructive">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="pt-6">
                <Button type="submit" disabled={loading} className="w-full h-12 rounded-full bg-primary font-bold">
                  {loading ? (isEditMode ? 'Saving...' : 'Registering...') : (isEditMode ? 'Save Changes' : 'Register Business')}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RegisterBusiness;
